import { useSyncExternalStore } from 'react'

/**
 * Which knobs the agent has just written, so the dock can show it happening.
 *
 * The agent's stream already carries every decision it makes — the role→tag
 * map, the KPI expression, the fitted parameters. Without this the dock would
 * only ever show the finished result, which loses the most useful thing about
 * watching an agent work: seeing WHICH knob it just turned, and therefore what
 * it is currently reasoning about. Marking a field here makes it sweep and
 * settle in the panel.
 *
 * Keys are dotted paths matching the dock's fields (`kpi.expr`, `drivers`,
 * `roles.hp_stm_temp`, `windows.baseline`). A key marked twice re-triggers the
 * animation, because the agent changing its mind is worth seeing too.
 */

/** How long a write stays "fresh" — long enough to notice, short enough that a
 *  panel of settled values doesn't shimmer forever. */
const FRESH_MS = 2200

interface WriteState {
  /** key → monotonically increasing sequence number of the write. */
  marks: Map<string, number>
  /** Bumped per write so the stagger order follows the agent's own order. */
  seq: number
}

let state: WriteState = { marks: new Map(), seq: 0 }
const listeners = new Set<() => void>()
const emit = (): void => listeners.forEach((listener) => listener())
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
const snapshot = (): WriteState => state

let expiry: ReturnType<typeof setTimeout> | null = null

/** Record that the agent set these fields. Order is preserved as the stagger. */
export const markAgentWrites = (keys: string[]): void => {
  if (!keys.length) return
  const marks = new Map(state.marks)
  let seq = state.seq
  for (const key of keys) marks.set(key, ++seq)
  state = { marks, seq }
  emit()
  // One shared timer rather than one per key: the whole batch settles together,
  // and a burst of writes cannot pile up hundreds of timeouts.
  if (expiry) clearTimeout(expiry)
  expiry = setTimeout(() => {
    expiry = null
    state = { marks: new Map(), seq: state.seq }
    emit()
  }, FRESH_MS)
}

/** Drop every mark — used when the dock closes or the model changes. */
export const clearAgentWrites = (): void => {
  if (!state.marks.size) return
  if (expiry) clearTimeout(expiry)
  expiry = null
  state = { marks: new Map(), seq: state.seq }
  emit()
}

/**
 * Freshness for one key: `null` when settled, otherwise its position within the
 * current burst so each field can be delayed a beat after the one before it.
 */
export const useFreshness = (key: string): number | null => {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot)
  const mark = current.marks.get(key)
  if (mark === undefined) return null
  // Position within this burst, capped so a 40-tag pull does not take a minute
  // to finish animating.
  const earliest = Math.min(...current.marks.values())
  return Math.min(mark - earliest, 12)
}

/** Whether anything at all is mid-animation, for the tab strip's live pill. */
export const useAgentWriting = (): boolean =>
  useSyncExternalStore(subscribe, snapshot, snapshot).marks.size > 0

/** Which dock tab owns a given knob, so the panel can follow the agent. */
export const tabForKey = (key: string): string | null => {
  if (key.startsWith('kpi.') || key === 'smoothing' || key === 'gate') {
    return 'config'
  }
  if (
    key === 'drivers' ||
    key.startsWith('driver.') ||
    key.startsWith('roles.')
  ) {
    return 'fit'
  }
  if (key.startsWith('windows.')) return 'config'
  if (key === 'fit.stats') return 'config'
  return null
}

/**
 * The tab the agent most recently wrote into, so the dock can jump there as the
 * decision lands. Returns the newest mark's tab, which is the one still being
 * animated.
 */
export const useAgentFocusTab = (): { tab: string; seq: number } | null => {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot)
  let best: { tab: string; seq: number } | null = null
  for (const [key, seq] of current.marks) {
    const tab = tabForKey(key)
    if (tab && (!best || seq > best.seq)) best = { tab, seq }
  }
  return best
}

/**
 * Translate one streamed agent event into the knob keys it wrote.
 *
 * Kept beside the store rather than in the chat hook so the mapping between
 * "what the agent emitted" and "which knob lights up" lives in one place.
 */
export const writeKeysForEvent = (event: {
  kind: string
  roles?: Record<string, string>
  expr?: string
  drivers?: string[]
  gate_expr?: string
  baseline_window?: unknown
  smoothing_days?: number
}): string[] => {
  switch (event.kind) {
    case 'roles':
      return Object.keys(event.roles ?? {}).map((role) => `roles.${role}`)
    case 'kpi':
      return ['kpi.name', 'kpi.expr']
    case 'fit': {
      const keys = ['fit.stats']
      if (event.drivers?.length) {
        keys.push('drivers', ...event.drivers.map((d) => `driver.${d}`))
      }
      if (event.gate_expr) keys.push('gate')
      if (event.baseline_window) keys.push('windows.baseline')
      if (event.smoothing_days) keys.push('smoothing')
      return keys
    }
    default:
      return []
  }
}
